import type { IndicatorBandingConfig } from '@risksmart-app/domain/src/types/indicator-banding-config';

export interface FrozenBandStamp {
  BandingConfigId?: string | null;
  BandLabel?: string | null;
  BandPolarity?: string | null;
  BandSeverity?: number | null;
}

export interface FrozenBandDisplay {
  label: string;
  colour: string;
  polarity: 'good' | 'bad';
  severity: number;
}

/**
 * Resolves PolarityBadge display props for a FROZEN per-result stamp —
 * never re-evaluates against the indicator's current config. The stamp
 * carries label/polarity/severity directly, but not colour, so this looks
 * up the band matching label+polarity within the PINNED config version
 * (via `configById`, keyed by BandingConfigId — see
 * useGetIndicatorBandingConfigsByIds) to recover it. Matching on
 * label+polarity together (not label alone) disambiguates a seeded
 * config's two same-labelled bands (e.g. "Outside Tolerance" appears on
 * both the low and high side) — though for a seeded config both sides
 * share the same colour regardless, so it wouldn't visibly matter if the
 * match landed on the "wrong" side.
 *
 * Returns null for "NotSet": an unstamped result (no numeric value, or no
 * config existed at log time), a coverage-gap stamp (pinned to a config
 * but the value matched no band), or a pinned config that hasn't loaded /
 * no longer resolves.
 */
export const resolveFrozenBandDisplay = (
  stamp: FrozenBandStamp,
  configById: Map<string, { config: IndicatorBandingConfig }>
): FrozenBandDisplay | null => {
  if (
    !stamp.BandingConfigId ||
    !stamp.BandLabel ||
    !stamp.BandPolarity ||
    stamp.BandSeverity == null
  ) {
    return null;
  }

  const pinned = configById.get(stamp.BandingConfigId);
  if (!pinned) {
    return null;
  }

  const band =
    pinned.config.bands.find(
      (b) => b.label === stamp.BandLabel && b.polarity === stamp.BandPolarity
    ) ?? pinned.config.bands.find((b) => b.label === stamp.BandLabel);
  if (!band) {
    return null;
  }

  return {
    label: stamp.BandLabel,
    colour: band.colour,
    polarity: stamp.BandPolarity === 'good' ? 'good' : 'bad',
    severity: stamp.BandSeverity,
  };
};
